import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const EmployeeReferrals = () => {
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [me, setMe] = useState(null);
  const [summary, setSummary] = useState(null);
  const [usages, setUsages] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });

  const fetchMyReferrals = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken(true);
      if (!token) {
        toast.error('Authentication required. Please login again.');
        navigate('/dashboard/login');
        return;
      }

      let url = `/api/referrals/my-stats?from=${dateRange.from}&to=${dateRange.to}`;
      if (statusFilter) url += `&status=${statusFilter}`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json().catch(() => ({ success: false, message: 'Invalid server response' }));
      if (!response.ok || !data?.success) {
        const message = data?.message || `Request failed (${response.status})`;
        if (response.status === 401) {
          toast.error('Session expired. Please login again.');
          navigate('/dashboard/login');
        } else {
          toast.error(message);
        }
        return;
      }

      setMe(data.data?.me || null);
      setSummary(data.data?.summary || null);
      setUsages(Array.isArray(data.data?.usages) ? data.data.usages : []);
    } catch (error) {
      console.error('My referral report error:', error);
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  }, [dateRange.from, dateRange.to, getToken, navigate, statusFilter]);

  useEffect(() => {
    fetchMyReferrals();
  }, [fetchMyReferrals]);

  const formatDate = (value) => {
    if (!value) return 'N/A';
    return new Date(value).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (value) => Number(value || 0).toFixed(2);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">My Referrals</h1>
        <p className="text-gray-600 mt-1">Track your own referral performance and rewards.</p>
      </div>

      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">Employee</p>
            <p className="text-sm font-semibold text-gray-900">{me?.name || 'N/A'}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">My Referral Code</p>
            <p className="text-sm font-semibold text-blue-700">{me?.referral_code || 'N/A'}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">Reference No</p>
            <p className="text-sm font-semibold text-gray-900">{me?.user_ref_no || 'N/A'}</p>
          </div>
        </div>
      </div>

      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange((prev) => ({ ...prev, from: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange((prev) => ({ ...prev, to: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Referral Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="">All Statuses</option>
              <option value="APPLIED">Applied</option>
              <option value="COMPLETED">Completed</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="REVERSED">Reversed</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={fetchMyReferrals}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-500">Total Referrals</p>
          <p className="text-2xl font-bold text-gray-900">{summary?.total_referrals || 0}</p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-500">Applied</p>
          <p className="text-2xl font-bold text-blue-600">{summary?.applied_count || 0}</p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-500">Completed</p>
          <p className="text-2xl font-bold text-green-600">{summary?.completed_count || 0}</p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-500">Cancelled / Reversed</p>
          <p className="text-2xl font-bold text-red-600">{(summary?.cancelled_count || 0) + (summary?.reversed_count || 0)}</p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-500">Reward Value</p>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary?.total_reward_value)}</p>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Referral Usage List</h2>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Applied At</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Used By</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reward</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td className="px-4 py-6 text-sm text-gray-500" colSpan={7}>Loading...</td>
                </tr>
              ) : usages.length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-sm text-gray-500" colSpan={7}>No referral records found for this range.</td>
                </tr>
              ) : (
                usages.map((row) => (
                  <tr key={row.id}>
                    <td className="px-4 py-3 text-sm text-gray-900">{formatDate(row.created_at)}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900">{row.referral_code || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div>{row.used_by_name || 'N/A'}</div>
                      <div className="text-xs text-gray-500">{row.used_by_email || 'N/A'}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{row.order_number || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{row.order_status || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{row.payment_status || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div>{row.status || 'N/A'}</div>
                      <div className="text-xs text-gray-500">{row.reward_type || 'N/A'} / {formatCurrency(row.reward_value)}</div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EmployeeReferrals;
